// src/services/pdfService.js
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// =========================
// FUENTES BASE64
// =========================
import promptFontBase64 from "./fonts/promptFontBase64";
import promptBoldFontBase64 from "./fonts/promptBoldFontBase64";
import expletusSansBase64 from "./fonts/expletusSansBase64";

// =========================
// COLORES EPC
// =========================
const COLORS = {
  primary: [0, 168, 153],
  primaryDark: [0, 140, 128],
  primaryLight: [0, 200, 180],
  accent: [100, 210, 195],
  white: [255, 255, 255],
  lightGray: [245, 247, 250],
  mediumGray: [150, 150, 150],
  darkGray: [90, 90, 90],
  textDark: [45, 45, 45],
  softLine: [200, 210, 205],
};

// =========================
// HELPERS
// =========================
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const loadImage = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.src = src;
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
  });

// =========================
// FORMATEAR NÚMERO DE FACTURA
// =========================
const formatInvoiceNumber = (
  invoiceNumber,
  issueDate,
  isProforma = false,
  isAbono = false,
  isLiquidacion = false,
  fraction = "",
) => {
  let prefix = "INV";
  if (isProforma) prefix = "PROFORMA";
  else if (isLiquidacion) prefix = "LIQ";
  else if (isAbono) prefix = "ABN";

  if (!invoiceNumber || invoiceNumber === "BORRADOR")
    return `${prefix}/BORRADOR${fraction ? `-${fraction}` : ""}`;

  // Si ya viene formateado, respetarlo y añadir fracción si aplica
  if (
    invoiceNumber.startsWith("INV/") ||
    invoiceNumber.startsWith("PROFORMA/") ||
    invoiceNumber.startsWith("ABN/") ||
    invoiceNumber.startsWith("LIQ/")
  ) {
    return fraction && !invoiceNumber.endsWith(`-${fraction}`)
      ? `${invoiceNumber}-${fraction}`
      : invoiceNumber;
  }

  const year = issueDate
    ? new Date(issueDate).getFullYear()
    : new Date().getFullYear();
  const number = String(invoiceNumber).padStart(4, "0");
  return `${prefix}/${year}/${number}${fraction ? `-${fraction}` : ""}`;
};

const extractRawNumber = (invoiceNumber) => {
  if (!invoiceNumber) return "";
  const str = String(invoiceNumber);
  const withoutPrefix = str.replace(/^[A-Z]+\//i, "");
  const withoutYear = withoutPrefix.replace(/^\d{4}\//, "");
  return withoutYear;
};

// Datos efectivos del comercial a mostrar en el PDF
const getCommercialDisplayData = (invoice) => {
  if (invoice.assignToCompany) {
    return {
      name: invoice.commercialCompanyName || invoice.commercialName || "—",
      taxIdLabel: "CIF",
      taxId: invoice.commercialCompanyTaxId || "",
      address:
        invoice.commercialCompanyAddress || invoice.commercialAddress || "",
    };
  }
  return {
    name: invoice.commercialName || "—",
    taxIdLabel: "DNI",
    taxId: invoice.commercialTaxId || "",
    address: invoice.commercialAddress || "",
  };
};

// Nombre del proyecto en mayúsculas
const getProjectUpper = (line) => {
  const name = line.projectName || line.projectTitle || line.projectId || "—";
  return String(name).toUpperCase();
};

// Determina si una liquidación es de cambio de comercializadora
const isSwitchLiquidation = (invoice) =>
  invoice.invoiceType === "liquidacion" &&
  invoice.liquidationMode === "switch";

// =========================
// GENERAR PDF (factura/proforma/abono/liquidación)
// =========================
export const generateInvoicePDF = async (invoice) => {
  const isProforma = invoice.invoiceType === "proforma";
  const isAbono = invoice.invoiceType === "abono";
  const isLiquidacion = invoice.invoiceType === "liquidacion";
  const isLiquidationSwitch = isSwitchLiquidation(invoice);

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const centerX = pageWidth / 2;
  const margin = 20;

  doc.addFileToVFS("Prompt-Regular.ttf", promptFontBase64);
  doc.addFont("Prompt-Regular.ttf", "Prompt", "normal");

  doc.addFileToVFS("Prompt-Bold.ttf", promptBoldFontBase64);
  doc.addFont("Prompt-Bold.ttf", "Prompt", "bold");

  doc.addFileToVFS("ExpletusSans-Bold.ttf", expletusSansBase64);
  doc.addFont("ExpletusSans-Bold.ttf", "ExpletusSans", "bold");

  const bgImage = await loadImage(`${process.env.PUBLIC_URL}/fondo.PNG`);
  const logoImage = await loadImage(`${process.env.PUBLIC_URL}/logoepc.png`);
  const sealImage = isLiquidacion
    ? await loadImage(`${process.env.PUBLIC_URL}/firma_sello.png`)
    : null;

  if (bgImage) {
    doc.addImage(
      bgImage,
      "PNG",
      0,
      0,
      pageWidth,
      pageHeight,
      undefined,
      "FAST",
    );
  }
  doc.setFillColor(255, 255, 255);
  doc.setGState(new doc.GState({ opacity: 0.75 }));
  doc.rect(0, 0, pageWidth, pageHeight, "F");
  doc.setGState(new doc.GState({ opacity: 1 }));

  // HEADER
  let currentY = 18;

  if (logoImage) {
    const logoWidth = 30;
    const logoHeight = (logoImage.height * logoWidth) / logoImage.width;
    doc.addImage(logoImage, "PNG", margin, currentY, logoWidth, logoHeight);
  }

  const formattedInvoiceNumber = formatInvoiceNumber(
    invoice.invoiceNumber,
    invoice.issueDate,
    isProforma,
    isAbono,
    isLiquidacion,
    invoice.fraction || "",
  );
  const lineY = 60;
  const titleY = lineY - 15;
  doc.setFont("ExpletusSans", "bold");
  doc.setFontSize(isProforma || isLiquidacion ? 24 : 36);
  doc.setTextColor(...COLORS.primary);
  doc.text(formattedInvoiceNumber, pageWidth - margin, titleY, {
    align: "right",
  });

  currentY = lineY;
  doc.setDrawColor(...COLORS.primaryLight);
  doc.setLineWidth(0.5);
  doc.line(margin, currentY, pageWidth - margin, currentY);

  // EMISOR + CLIENTE/COMERCIAL
  currentY = 77;
  const colWidth = (pageWidth - margin * 2 - 20) / 2;

  doc.setFont("ExpletusSans", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.primary);
  doc.text("EMISOR", margin, currentY);

  doc.setFont("Prompt", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.textDark);
  currentY += 7;
  doc.text(
    invoice.issuerName || "EPC Development & Management, S.L.",
    margin,
    currentY,
  );
  currentY += 5.5;
  doc.text(`CIF: ${invoice.issuerTaxId || "B26623850"}`, margin, currentY);
  currentY += 5.5;
  doc.text(
    invoice.issuerAddress || "C/ Juan de Austria 13, Cox, Alicante",
    margin,
    currentY,
  );

  const clientX = margin + colWidth + 20;
  currentY = 77;
  doc.setFont("ExpletusSans", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.primary);
  doc.text(isLiquidacion ? "COMERCIAL" : "CLIENTE", clientX, currentY);

  doc.setFont("Prompt", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.textDark);
  currentY += 7;

  if (isLiquidacion) {
    const d = getCommercialDisplayData(invoice);
    doc.text(d.name, clientX, currentY);
    currentY += 5.5;
    if (d.taxId) {
      doc.text(`${d.taxIdLabel}: ${d.taxId}`, clientX, currentY);
      currentY += 5.5;
    }
    if (d.address) {
      const splitAddress = doc.splitTextToSize(d.address, colWidth);
      doc.text(splitAddress, clientX, currentY);
    }
  } else {
    doc.text(invoice.clientName || "—", clientX, currentY);
    currentY += 5.5;
    if (invoice.clientTaxId) {
      doc.text(`CIF/NIF: ${invoice.clientTaxId}`, clientX, currentY);
      currentY += 5.5;
    }
    if (invoice.clientAddress) {
      const splitAddress = doc.splitTextToSize(
        invoice.clientAddress,
        colWidth,
      );
      doc.text(splitAddress, clientX, currentY);
    }
  }

  // FECHAS
  currentY = 105;

  doc.setFont("Prompt", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.darkGray);
  doc.text("Fecha de emisión:", margin, currentY);
  doc.setFont("Prompt", "bold");
  doc.setTextColor(...COLORS.textDark);
  doc.text(formatDate(invoice.issueDate), margin + 35, currentY);

  currentY += 6;
  doc.setFont("Prompt", "normal");
  doc.setTextColor(...COLORS.darkGray);
  doc.text("Fecha de vencimiento:", margin, currentY);
  doc.setFont("Prompt", "bold");
  doc.setTextColor(...COLORS.textDark);
  doc.text(
    invoice.dueDate ? formatDate(invoice.dueDate) : "—",
    margin + 40,
    currentY,
  );

  // TABLA
  currentY = 117;
  let finalTableY = currentY;

  if (invoice.lines && invoice.lines.length > 0) {
    if (isLiquidationSwitch) {
      // ==========================================
      // LIQUIDACIÓN - CAMBIO DE COMERCIALIZADORA
      // (sin columna trimestre, con CUPS y fecha entrada)
      // ==========================================
      autoTable(doc, {
        startY: currentY,
        head: [["Cliente", "CUPS", "Fecha entrada", "Comisión sin IVA"]],
        body: invoice.lines.map((line) => [
          line.switchClientName || "—",
          line.cups || "—",
          line.fechaEntrada ? formatDate(line.fechaEntrada) : "—",
          formatCurrency(line.commission),
        ]),
        theme: "plain",
        tableWidth: pageWidth - margin * 2,
        styles: {
          font: "Prompt",
          fontSize: 8.5,
          cellPadding: 3.5,
          textColor: COLORS.textDark,
          fillColor: null,
          lineWidth: 0,
        },
        headStyles: {
          fillColor: null,
          textColor: COLORS.primary,
          fontStyle: "bold",
          halign: "center",
          fontSize: 8.5,
          lineWidth: { bottom: 0.5, top: 0, left: 0, right: 0 },
          lineColor: COLORS.primary,
        },
        bodyStyles: { fillColor: null, lineWidth: 0 },
        columnStyles: {
          0: { halign: "left", cellWidth: "auto" },
          1: { halign: "left", cellWidth: 55 },
          2: { halign: "center", cellWidth: 32 },
          3: { halign: "right", cellWidth: 40 },
        },
        margin: { left: margin, right: margin },
        didDrawPage: () => {
          const pageCount = doc.getNumberOfPages();
          const footerY = pageHeight - 15;
          doc.setDrawColor(...COLORS.primary);
          doc.setLineWidth(0.6);
          doc.line(margin, footerY, pageWidth - margin, footerY);
          doc.setFontSize(7);
          doc.setFont("Prompt", "normal");
          doc.setTextColor(...COLORS.mediumGray);
          doc.text(
            "EPC Development & Management, S.L. — B26623850",
            centerX,
            footerY + 5,
            { align: "center" },
          );
          doc.text(`Página ${pageCount}`, pageWidth - margin, footerY + 5, {
            align: "right",
          });
        },
      });
    } else if (isLiquidacion) {
      // ==========================================
      // LIQUIDACIÓN - POR PROYECTO (original)
      // ==========================================
      autoTable(doc, {
        startY: currentY,
        head: [["Proyecto", "Trimestre", "Comisión sin IVA"]],
        body: invoice.lines.map((line) => [
          getProjectUpper(line),
          line.quarter || "—",
          formatCurrency(line.commission),
        ]),
        theme: "plain",
        tableWidth: pageWidth - margin * 2,
        styles: {
          font: "Prompt",
          fontSize: 9,
          cellPadding: 4,
          textColor: COLORS.textDark,
          fillColor: null,
          lineWidth: 0,
        },
        headStyles: {
          fillColor: null,
          textColor: COLORS.primary,
          fontStyle: "bold",
          halign: "center",
          fontSize: 9,
          lineWidth: { bottom: 0.5, top: 0, left: 0, right: 0 },
          lineColor: COLORS.primary,
        },
        bodyStyles: { fillColor: null, lineWidth: 0 },
        columnStyles: {
          0: { halign: "left", cellWidth: "auto" },
          1: { halign: "center", cellWidth: 30 },
          2: { halign: "right", cellWidth: 50 },
        },
        margin: { left: margin, right: margin },
        didDrawPage: () => {
          const pageCount = doc.getNumberOfPages();
          const footerY = pageHeight - 15;
          doc.setDrawColor(...COLORS.primary);
          doc.setLineWidth(0.6);
          doc.line(margin, footerY, pageWidth - margin, footerY);
          doc.setFontSize(7);
          doc.setFont("Prompt", "normal");
          doc.setTextColor(...COLORS.mediumGray);
          doc.text(
            "EPC Development & Management, S.L. — B26623850",
            centerX,
            footerY + 5,
            { align: "center" },
          );
          doc.text(`Página ${pageCount}`, pageWidth - margin, footerY + 5, {
            align: "right",
          });
        },
      });
    } else {
      // ==========================================
      // FACTURA / PROFORMA / ABONO (original)
      // ==========================================
      autoTable(doc, {
        startY: currentY,
        head: [["Concepto", "Cant.", "Precio ud.", "IVA", "Total"]],
        body: invoice.lines.map((line) => [
          line.description || "—",
          String(line.quantity || 1),
          formatCurrency(line.unitPrice),
          `${line.taxRate || 21}%`,
          formatCurrency(line.total),
        ]),
        theme: "plain",
        tableWidth: pageWidth - margin * 2,
        styles: {
          font: "Prompt",
          fontSize: 9,
          cellPadding: 4,
          textColor: COLORS.textDark,
          fillColor: null,
          lineWidth: 0,
        },
        headStyles: {
          fillColor: null,
          textColor: COLORS.primary,
          fontStyle: "bold",
          halign: "center",
          fontSize: 9,
          lineWidth: { bottom: 0.5, top: 0, left: 0, right: 0 },
          lineColor: COLORS.primary,
        },
        bodyStyles: { fillColor: null, lineWidth: 0 },
        columnStyles: {
          0: { halign: "left", cellWidth: "auto" },
          1: { halign: "center", cellWidth: 18 },
          2: { halign: "right", cellWidth: 28 },
          3: { halign: "center", cellWidth: 16 },
          4: { halign: "right", cellWidth: 28 },
        },
        margin: { left: margin, right: margin },
        didDrawPage: () => {
          const pageCount = doc.getNumberOfPages();
          const footerY = pageHeight - 15;
          doc.setDrawColor(...COLORS.primary);
          doc.setLineWidth(0.6);
          doc.line(margin, footerY, pageWidth - margin, footerY);
          doc.setFontSize(7);
          doc.setFont("Prompt", "normal");
          doc.setTextColor(...COLORS.mediumGray);
          doc.text(
            "EPC Development & Management, S.L. — B26623850",
            centerX,
            footerY + 5,
            { align: "center" },
          );
          doc.text(`Página ${pageCount}`, pageWidth - margin, footerY + 5, {
            align: "right",
          });
        },
      });
    }
    finalTableY = doc.lastAutoTable?.finalY || currentY;
  }

  finalTableY += 2;
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.5);
  doc.line(margin, finalTableY, pageWidth - margin, finalTableY);

  // TOTALES
  currentY = finalTableY + 12;
  const totalsLabelX = pageWidth - margin - 55;
  const totalsValueX = pageWidth - margin;

  doc.setFont("Prompt", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.textDark);
  doc.text(
    isLiquidacion ? "Comisión sin IVA:" : "Base imponible:",
    totalsLabelX,
    currentY,
  );
  doc.text(formatCurrency(invoice.baseAmount), totalsValueX, currentY, {
    align: "right",
  });

  if (!isLiquidacion) {
    currentY += 7;
    doc.text(`IVA (${invoice.taxRate || 21}%):`, totalsLabelX, currentY);
    doc.text(formatCurrency(invoice.taxAmount), totalsValueX, currentY, {
      align: "right",
    });
  }

  currentY += 6;
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.5);
  doc.line(totalsLabelX, currentY, totalsValueX, currentY);

  currentY += 8;
  doc.setFont("ExpletusSans", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...COLORS.primary);
  doc.text("TOTAL:", totalsLabelX, currentY);
  doc.text(formatCurrency(invoice.total), totalsValueX, currentY, {
    align: "right",
  });

  // FORMA DE PAGO — OCULTA EN LIQUIDACIONES
  if (!isLiquidacion) {
    currentY += 20;
    doc.setFont("ExpletusSans", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.primary);
    doc.text("FORMA DE PAGO", margin, currentY);

    doc.setFont("Prompt", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...COLORS.textDark);
    currentY += 7;
    doc.text(
      `Método: ${invoice.paymentMethod === "transfer" ? "Transferencia bancaria" : "Al contado"}`,
      margin,
      currentY,
    );
    if (invoice.iban) {
      currentY += 6;
      doc.text(`IBAN: ${invoice.iban}`, margin, currentY);
    }
    currentY += 6;
    doc.text(
      `Vencimiento: ${invoice.dueDate ? formatDate(invoice.dueDate) : "—"}`,
      margin,
      currentY,
    );
  }

  // NOTAS
  if (invoice.notes) {
    currentY += 15;
    doc.setFont("ExpletusSans", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.primary);
    doc.text("NOTAS", margin, currentY);

    currentY += 6;
    doc.setFont("Prompt", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.textDark);
    const splitNotes = doc.splitTextToSize(
      invoice.notes,
      pageWidth - margin * 2,
    );
    doc.text(splitNotes, margin, currentY);
  }

  // SELLO + FIRMA COMERCIAL (solo liquidación)
  if (isLiquidacion) {
    const sealTopY = pageHeight - 65;

    doc.setFont("ExpletusSans", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.primary);
    doc.text(
      "Firma EPC Development & Management, S.L.",
      margin,
      sealTopY - 3,
    );

    if (sealImage) {
      const sealWidth = 45;
      const sealHeight = (sealImage.height * sealWidth) / sealImage.width;
      doc.addImage(sealImage, "PNG", margin, sealTopY, sealWidth, sealHeight);
    }

    const signX = pageWidth / 2 + 10;
    doc.setFont("ExpletusSans", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.primary);
    doc.text("Firma del comercial", signX, sealTopY - 3);
  }

  // PIE DE PÁGINA FINAL
  const footerY = pageHeight - 15;
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.6);
  doc.line(margin, footerY, pageWidth - margin, footerY);
  doc.setFontSize(7);
  doc.setFont("Prompt", "normal");
  doc.setTextColor(...COLORS.mediumGray);
  doc.text(
    "EPC Development & Management, S.L. — B26623850",
    centerX,
    footerY + 5,
    { align: "center" },
  );

  // GUARDAR PDF
  let prefix = "Factura";
  if (isProforma) prefix = "Proforma";
  else if (isLiquidacion) prefix = "Liquidacion";
  else if (isAbono) prefix = "Abono";

  const fractionSuffix =
    isProforma && invoice.fraction ? `-${invoice.fraction}` : "";

  const fileName = `${prefix}_${invoice.invoiceNumber || "borrador"}${fractionSuffix}_${
    invoice.clientName?.replace(/\s+/g, "_") ||
    invoice.commercialName?.replace(/\s+/g, "_") ||
    "destinatario"
  }.pdf`;

  doc.save(fileName);
};

// =========================
// GENERAR PDF AUTOFACTURA
// =========================
export const generateSelfInvoicePDF = async (invoice) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const centerX = pageWidth / 2;
  const margin = 20;

  doc.addFileToVFS("Prompt-Regular.ttf", promptFontBase64);
  doc.addFont("Prompt-Regular.ttf", "Prompt", "normal");

  doc.addFileToVFS("Prompt-Bold.ttf", promptBoldFontBase64);
  doc.addFont("Prompt-Bold.ttf", "Prompt", "bold");

  doc.addFileToVFS("ExpletusSans-Bold.ttf", expletusSansBase64);
  doc.addFont("ExpletusSans-Bold.ttf", "ExpletusSans", "bold");

  const bgImage = await loadImage(`${process.env.PUBLIC_URL}/fondo.PNG`);
  const logoImage = await loadImage(`${process.env.PUBLIC_URL}/logoepc.png`);

  if (bgImage) {
    doc.addImage(
      bgImage,
      "PNG",
      0,
      0,
      pageWidth,
      pageHeight,
      undefined,
      "FAST",
    );
  }
  doc.setFillColor(255, 255, 255);
  doc.setGState(new doc.GState({ opacity: 0.75 }));
  doc.rect(0, 0, pageWidth, pageHeight, "F");
  doc.setGState(new doc.GState({ opacity: 1 }));

  // HEADER
  let currentY = 18;

  if (logoImage) {
    const logoWidth = 30;
    const logoHeight = (logoImage.height * logoWidth) / logoImage.width;
    doc.addImage(logoImage, "PNG", margin, currentY, logoWidth, logoHeight);
  }

  // Número autofactura: AUTO/YYYY/XXXX
  const rawNumber = extractRawNumber(invoice.invoiceNumber);
  const year = invoice.issueDate
    ? new Date(invoice.issueDate).getFullYear()
    : new Date().getFullYear();
  const selfInvoiceNumber = `AUTO/${year}/${String(rawNumber || "0000").padStart(
    4,
    "0",
  )}`;

  const lineY = 60;
  const titleY = lineY - 15;
  doc.setFont("ExpletusSans", "bold");
  doc.setFontSize(24);
  doc.setTextColor(...COLORS.primary);
  doc.text(selfInvoiceNumber, pageWidth - margin, titleY, {
    align: "right",
  });

  currentY = lineY;
  doc.setDrawColor(...COLORS.primaryLight);
  doc.setLineWidth(0.5);
  doc.line(margin, currentY, pageWidth - margin, currentY);

  // EMISOR = COMERCIAL, CLIENTE = EPC
  currentY = 77;
  const colWidth = (pageWidth - margin * 2 - 20) / 2;

  const d = getCommercialDisplayData(invoice);

  doc.setFont("ExpletusSans", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.primary);
  doc.text("EMISOR", margin, currentY);

  doc.setFont("Prompt", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.textDark);
  currentY += 7;
  doc.text(d.name, margin, currentY);
  currentY += 5.5;
  if (d.taxId) {
    doc.text(`${d.taxIdLabel}: ${d.taxId}`, margin, currentY);
    currentY += 5.5;
  }
  if (d.address) {
    const split = doc.splitTextToSize(d.address, colWidth);
    doc.text(split, margin, currentY);
  }

  const clientX = margin + colWidth + 20;
  currentY = 77;
  doc.setFont("ExpletusSans", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.primary);
  doc.text("CLIENTE", clientX, currentY);

  doc.setFont("Prompt", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.textDark);
  currentY += 7;
  doc.text(
    invoice.issuerName || "EPC Development & Management, S.L.",
    clientX,
    currentY,
  );
  currentY += 5.5;
  doc.text(`CIF: ${invoice.issuerTaxId || "B26623850"}`, clientX, currentY);
  currentY += 5.5;
  doc.text(
    invoice.issuerAddress || "C/ Juan de Austria 13, Cox, Alicante",
    clientX,
    currentY,
  );

  // FECHAS
  currentY = 105;
  doc.setFont("Prompt", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.darkGray);
  doc.text("Fecha de emisión:", margin, currentY);
  doc.setFont("Prompt", "bold");
  doc.setTextColor(...COLORS.textDark);
  doc.text(formatDate(invoice.issueDate), margin + 35, currentY);

  currentY += 6;
  doc.setFont("Prompt", "normal");
  doc.setTextColor(...COLORS.darkGray);
  doc.text("Fecha de vencimiento:", margin, currentY);
  doc.setFont("Prompt", "bold");
  doc.setTextColor(...COLORS.textDark);
  doc.text(
    invoice.dueDate ? formatDate(invoice.dueDate) : "—",
    margin + 40,
    currentY,
  );

  // TABLA
  currentY = 117;
  let finalTableY = currentY;

  if (invoice.lines && invoice.lines.length > 0) {
    autoTable(doc, {
      startY: currentY,
      head: [["Proyecto", "Trimestre", "Comisión"]],
      body: invoice.lines.map((line) => [
        getProjectUpper(line),
        line.quarter || "—",
        formatCurrency(line.commission),
      ]),
      theme: "plain",
      tableWidth: pageWidth - margin * 2,
      styles: {
        font: "Prompt",
        fontSize: 9,
        cellPadding: 4,
        textColor: COLORS.textDark,
        fillColor: null,
        lineWidth: 0,
      },
      headStyles: {
        fillColor: null,
        textColor: COLORS.primary,
        fontStyle: "bold",
        halign: "center",
        fontSize: 9,
        lineWidth: { bottom: 0.5, top: 0, left: 0, right: 0 },
        lineColor: COLORS.primary,
      },
      bodyStyles: { fillColor: null, lineWidth: 0 },
      columnStyles: {
        0: { halign: "left", cellWidth: "auto" },
        1: { halign: "center", cellWidth: 30 },
        2: { halign: "right", cellWidth: 50 },
      },
      margin: { left: margin, right: margin },
      didDrawPage: () => {
        const pageCount = doc.getNumberOfPages();
        const footerY = pageHeight - 15;
        doc.setDrawColor(...COLORS.primary);
        doc.setLineWidth(0.6);
        doc.line(margin, footerY, pageWidth - margin, footerY);
        doc.setFontSize(7);
        doc.setFont("Prompt", "normal");
        doc.setTextColor(...COLORS.mediumGray);
        doc.text(
          "EPC Development & Management, S.L. — B26623850",
          centerX,
          footerY + 5,
          { align: "center" },
        );
        doc.text(`Página ${pageCount}`, pageWidth - margin, footerY + 5, {
          align: "right",
        });
      },
    });
    finalTableY = doc.lastAutoTable?.finalY || currentY;
  }

  finalTableY += 2;
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.5);
  doc.line(margin, finalTableY, pageWidth - margin, finalTableY);

  // TOTALES (IVA 0)
  currentY = finalTableY + 12;
  const totalsLabelX = pageWidth - margin - 55;
  const totalsValueX = pageWidth - margin;

  doc.setFont("Prompt", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.textDark);
  doc.text("Base imponible:", totalsLabelX, currentY);
  doc.text(formatCurrency(invoice.baseAmount), totalsValueX, currentY, {
    align: "right",
  });

  currentY += 7;
  doc.text("IVA (0%):", totalsLabelX, currentY);
  doc.text(formatCurrency(0), totalsValueX, currentY, {
    align: "right",
  });

  currentY += 6;
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.5);
  doc.line(totalsLabelX, currentY, totalsValueX, currentY);

  currentY += 8;
  doc.setFont("ExpletusSans", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...COLORS.primary);
  doc.text("TOTAL:", totalsLabelX, currentY);
  doc.text(formatCurrency(invoice.baseAmount), totalsValueX, currentY, {
    align: "right",
  });

  // FORMA DE PAGO
  currentY += 20;
  doc.setFont("ExpletusSans", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.primary);
  doc.text("FORMA DE PAGO", margin, currentY);

  doc.setFont("Prompt", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.textDark);
  currentY += 7;
  doc.text("Método: Transferencia bancaria", margin, currentY);

  if (invoice.iban) {
    currentY += 6;
    doc.text(`IBAN: ${invoice.iban}`, margin, currentY);
  }

  // PIE DE PÁGINA FINAL
  const footerY = pageHeight - 15;
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.6);
  doc.line(margin, footerY, pageWidth - margin, footerY);
  doc.setFontSize(7);
  doc.setFont("Prompt", "normal");
  doc.setTextColor(...COLORS.mediumGray);
  doc.text(
    "EPC Development & Management, S.L. — B26623850",
    centerX,
    footerY + 5,
    { align: "center" },
  );

  // GUARDAR
  const fileName = `Autofactura_${selfInvoiceNumber.replace(/\//g, "-")}_${
    (invoice.assignToCompany
      ? invoice.commercialCompanyName
      : invoice.commercialName
    )?.replace(/\s+/g, "_") || "comercial"
  }.pdf`;
  doc.save(fileName);
};